import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './guidance.css';

const FAQPage = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Hero section slides
  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1545870490-5d5c3bef8f5c?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'Airport parking garage with available spaces',
      title: 'Airport Parking Made Simple',
      subtitle: 'Find and book secure airport parking in seconds with our specialized platform'
    },
    {
      image: 'https://images.unsplash.com/photo-1567801244563-9e7a273b21e9?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'Person using airport parking app on phone',
      title: 'Seamless Airport Experience',
      subtitle: 'From booking to takeoff - all your airport parking needs in one place'
    },
    {
      image: 'https://images.unsplash.com/photo-1572086730895-8ad6d57b73e9?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      alt: 'Meet and greet airport parking service',
      title: 'Premium Meet & Greet',
      subtitle: 'Experience our valet service at all major UK airports'
    }
  ];

  // Airport parking guidance questions
  const parkingQuestions = [
    {
      id: 1,
      question: "Which UK airport are you traveling from?",
      options: ["Heathrow", "Gatwick", "Manchester", "Stansted", "Luton", "Other"],
      icon: "✈️"
    },
    {
      id: 2,
      question: "What type of airport parking do you need?",
      options: ["Meet & Greet (Valet)", "Park & Ride", "On-Airport", "Off-site secure", "Long-stay"],
      icon: "🅿️"
    },
    {
      id: 3,
      question: "How long will you be parking?",
      options: ["1-3 days", "4-7 days", "8-14 days", "15+ days"],
      icon: "⏱️"
    },
    {
      id: 4,
      question: "What's most important for your airport parking?",
      options: ["Speed/convenience", "Security", "Price", "Proximity to terminal"],
      icon: "⭐"
    }
  ];

  // Airport parking guidance results
  const getParkingGuidance = () => {
    const airport = responses[1];
    const type = responses[2];
    const duration = responses[3];
    const priority = responses[4];

    const tips = [];

    // Airport-specific tips
    if (airport === "Heathrow" || airport === "Gatwick") {
      tips.push(`Book ${airport} parking at least 3 weeks in advance for the best rates - prices increase significantly closer to travel dates`);
    } else if (airport === "Manchester" || airport === "Stansted") {
      tips.push(`Consider off-site parking at ${airport} for savings of 20-40% compared to on-airport options`);
    }

    // Parking type tips
    if (type === "Meet & Greet (Valet)") {
      tips.push("Allow extra 15 minutes for meet & greet services during peak travel times (6-9AM, 4-7PM)");
    } else if (type === "Park & Ride") {
      tips.push("Check shuttle frequency - aim for services that run at least every 15 minutes to avoid terminal delays");
    }

    // Duration tips
    if (duration === "15+ days") {
      tips.push("Long-stay parking offers the best value for trips over 2 weeks - look for weekly rate discounts");
    }

    // Priority-based tips
    if (priority === "Speed/convenience") {
      tips.push("Meet & Greet is fastest but premium-priced. On-airport parking offers the best balance of convenience and value");
    } else if (priority === "Security") {
      tips.push("Choose Park Mark approved facilities with 24/7 security, CCTV, and perimeter fencing");
    } else if (priority === "Price") {
      tips.push("Off-site parking typically offers the best rates - book at least 2 weeks early for maximum savings");
    }

    return {
      title: "Your Airport Parking Strategy",
      message: "Based on your airport and travel needs, here are tailored recommendations:",
      tips: tips.length > 0 ? tips : [
        "Book at least 2-3 weeks in advance for the best airport parking rates",
        "Check for included services like luggage assistance and 24/7 customer support",
        "Verify the security features - look for Park Mark accreditation",
        "Consider traffic conditions - allow extra time during peak travel periods"
      ]
    };
  };

  // Airport parking FAQ questions
  const faqs = [
    {
      question: "How far in advance should I book airport parking?",
      answer: "We recommend booking at least 2-3 weeks before your travel date for the best rates and availability. For peak periods like holidays and summer weekends, book 4-6 weeks in advance. Last-minute bookings (within 48 hours) typically cost 20-40% more.",
      category: "Booking"
    },
    {
      question: "What's the difference between Meet & Greet and Park & Ride?",
      answer: "Meet & Greet (valet service): You drop your car at the terminal, and we park it for you. Most convenient but premium priced. Park & Ride: You park at a secure facility and take a shuttle bus to the terminal. More economical with frequent shuttles (typically every 15-20 minutes).",
      category: "Services"
    },
    {
      question: "How do I find the meet & greet meeting point?",
      answer: "After booking, you'll receive detailed instructions including the exact meeting point, operator name, and contact number. Most airports have dedicated meet & greet zones in short-stay car parks - follow signs for 'Valet Parking' or 'Meet & Greet'. Our drivers typically wait in the designated area with your name on a sign.",
      category: "Meet & Greet"
    },
    {
      question: "What security measures are in place?",
      answer: "All our partner car parks feature: 24/7 security patrols, CCTV coverage, perimeter fencing, secure entry/exit barriers, and Park Mark security accreditation. Many facilities also have alarm systems, regular patrols, and well-lit areas. Vehicles are fully insured while in our care.",
      category: "Security"
    },
    {
      question: "What happens if my flight is delayed?",
      answer: "No need to worry about flight delays. Our system automatically tracks flight arrivals and adjusts accordingly. There's no extra charge for reasonable delays (typically up to 4 hours). For longer delays, we recommend contacting the car park directly - most offer grace periods without additional fees.",
      category: "Flight Changes"
    },
    {
      question: "Do you offer electric vehicle charging?",
      answer: "Yes, many of our airport parking facilities now offer EV charging stations. You can filter for this option when searching. Most provide slow charging during your trip (typically included in parking price), while some offer fast charging for an additional fee. Please specify you need EV charging when booking.",
      category: "EV Services"
    }
  ];

  // Auto-rotate hero slides
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const toggleFAQ = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const handleSelectOption = (questionId, option) => {
    const newResponses = { ...responses, [questionId]: option };
    setResponses(newResponses);
    
    if (currentQuestion < parkingQuestions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(prev => prev + 1);
      }, 300);
    } else {
      setTimeout(() => {
        setShowResults(true);
      }, 500);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const restartQuestionnaire = () => {
    setCurrentQuestion(0);
    setResponses({});
    setShowResults(false);
  };

  // Animation variants
  const slideVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const questionVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  const optionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const guidance = getParkingGuidance();

  return (
    <div className="faq-page">
      {/* Section 1: Hero */}
      <section className="faq-hero" aria-label="Airport parking solutions hero section">
        <div className="faq-hero__bg-container">
          {slides.map((slide, index) => (
            <motion.div
              key={index}
              className={`faq-hero__bg ${currentSlide === index ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
              variants={slideVariants}
              initial="enter"
              animate={currentSlide === index ? "center" : "exit"}
              transition={{ duration: 1.5, ease: [0.76, 0, 0.24, 1] }}
              aria-hidden={currentSlide !== index}
            >
              <div className="faq-hero__overlay" />
            </motion.div>
          ))}
        </div>

        <div className="faq-hero__content">
          <motion.div
            className="faq-hero__content-inner"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <div className="faq-hero__title-container">
                <motion.span 
                  className="faq-hero__title-line"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.4, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                />
                <motion.h1 
                  className="faq-hero__title"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {slides[currentSlide].title}
                </motion.h1>
                <motion.span 
                  className="faq-hero__title-line faq-hero__title-line--right"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <motion.p 
                className="faq-hero__subtitle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {slides[currentSlide].subtitle}
              </motion.p>
            </motion.div>

            <motion.div 
              className="faq-hero__cta-container"
              variants={itemVariants}
            >
              <motion.a
                href="#parking-guide"
                className="faq-hero__cta-button"
                whileHover={{ 
                  y: -3,
                  boxShadow: "0 10px 25px -5px rgba(99, 102, 241, 0.3)"
                }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                Get Airport Parking Tips
                <svg className="faq-hero__cta-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </motion.a>
            </motion.div>
          </motion.div>
        </div>

        <div className="faq-hero__indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`faq-hero__indicator ${currentSlide === index ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Section 2: Airport Parking Guide */}
      <section id="parking-guide" className="parking-guide">
        <div className="parking-guide__container">
          <motion.div 
            className="parking-guide__header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <motion.span 
              className="parking-guide__subtitle"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              viewport={{ once: true }}
            >
              Airport Parking Guide
            </motion.span>
            <motion.h2 
              className="parking-guide__title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Optimize Your Airport Parking Experience
            </motion.h2>
            <motion.p 
              className="parking-guide__description"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              viewport={{ once: true }}
            >
              Answer a few questions to get personalized airport parking recommendations
            </motion.p>
          </motion.div>

          {!showResults ? (
            <motion.div 
              className="parking-wizard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              <div className="wizard-progress">
                <div 
                  className="progress-bar" 
                  style={{ 
                    width: `${((currentQuestion + 1) / parkingQuestions.length) * 100}%`,
                    background: `linear-gradient(90deg, #1e40af 0%, #3b82f6 100%)`
                  }} 
                />
                <span className="progress-text">Question {currentQuestion + 1} of {parkingQuestions.length}</span>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion}
                  className="question-container"
                  variants={questionVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.4 }}
                >
                  <div className="question-header">
                    <span className="question-icon">{parkingQuestions[currentQuestion].icon}</span>
                    <h3 className="question-text">{parkingQuestions[currentQuestion].question}</h3>
                  </div>
                  
                  <div className="options-grid">
                    {parkingQuestions[currentQuestion].options.map((option, index) => (
                      <motion.button
                        key={index}
                        className={`option-card ${responses[parkingQuestions[currentQuestion].id] === option ? 'selected' : ''}`}
                        onClick={() => handleSelectOption(parkingQuestions[currentQuestion].id, option)}
                        variants={optionVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ y: -3, boxShadow: "0 6px 12px rgba(0,0,0,0.1)" }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {option}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="wizard-navigation">
                <motion.button
                  className="back-button"
                  onClick={handleBack}
                  disabled={currentQuestion === 0}
                  whileHover={{ scale: currentQuestion === 0 ? 1 : 1.05 }}
                  whileTap={{ scale: currentQuestion === 0 ? 1 : 0.95 }}
                >
                  Back
                </motion.button>
                
                {currentQuestion === parkingQuestions.length - 1 && (
                  <motion.button
                    className="submit-button"
                    onClick={() => setShowResults(true)}
                    disabled={!responses[parkingQuestions[currentQuestion].id]}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    See Airport Parking Results
                  </motion.button>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div 
              className="guidance-results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="results-header">
                <h3>{guidance.title}</h3>
                <p>{guidance.message}</p>
              </div>
              
              <motion.div 
                className="results-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h4>Airport Parking Tips</h4>
                <div className="tips-grid">
                  {guidance.tips.map((tip, index) => (
                    <motion.div
                      key={index}
                      className="tip-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="tip-number">{index + 1}</div>
                      <p>{tip}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              
              <motion.button
                className="restart-button"
                onClick={restartQuestionnaire}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                Start New Airport Parking Questionnaire
              </motion.button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Section 3: Full Airport Parking FAQ */}
      <section className="full-faq">
        <div className="full-faq__container">
          <motion.div 
            className="full-faq__header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2 
              className="full-faq__title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              Airport Parking FAQs
            </motion.h2>
            <motion.p 
              className="full-faq__description"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              viewport={{ once: true }}
            >
              Everything you need to know about airport parking in the UK
            </motion.p>
          </motion.div>

          <div className="full-faq__list">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index}
                className="full-faq__item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)" }}
              >
                <motion.button
                  className="full-faq__question"
                  onClick={() => toggleFAQ(index)}
                  whileHover={{ backgroundColor: '#f0f9ff' }}
                >
                  <span>{faq.question}</span>
                  <motion.span
                    animate={{ rotate: activeIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                    </svg>
                  </motion.span>
                </motion.button>

                <AnimatePresence>
                  {activeIndex === index && (
                    <motion.div
                      className="full-faq__answer"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <p>{faq.answer}</p>
                      {index % 2 === 0 && (
                        <motion.div 
                          className="helpful-buttons"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          <span>Was this helpful?</span>
                          <motion.button whileTap={{ scale: 0.95 }}>👍 Yes</motion.button>
                          <motion.button whileTap={{ scale: 0.95 }}>👎 No</motion.button>
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Chat Bubble */}
      <motion.div
        className="chat-bubble"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 300, damping: 10 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
        </svg>
        <span className="pulse-effect"></span>
      </motion.div>
    </div>
  );
};

export default FAQPage;